import React from "react";
import { FiChevronDown, FiChevronUp, FiGift } from "react-icons/fi";
import { EmptyState } from "components/ui";
import { RedeemStepHeading } from "./RedeemStepHeading";

type Props = {
  brands: any[] | undefined;
  country: string;
  pointDisplay: any;
  hiddenBrands: string[];
  showAll: boolean;
  selectedBrand: string;
  error: string;
  isLoading: boolean;
  giftcardIcon: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onShowMore: () => void;
  onShowLess: () => void;
};

/** Step 1 of the redemption checkout: choose a gift card. Presentation only. */
export const RedeemBrandStep = ({
  brands,
  country,
  pointDisplay,
  hiddenBrands,
  showAll,
  selectedBrand,
  error,
  isLoading,
  giftcardIcon,
  onChange,
  onShowMore,
  onShowLess,
}: Props) => {
  const isEligible = (currentItem: any) =>
    currentItem.currency_name === country &&
    Number(pointDisplay) / 100 >= currentItem.min &&
    Number(pointDisplay) / 100 <= currentItem.max;

  const listed = brands?.filter((item) => !hiddenBrands.includes(item.name));
  const hasOptions = !!listed?.some(isEligible);

  return (
    <section className="zs-card">
      <div className="zs-card-header">
        <RedeemStepHeading step={1} title="Choose a gift card" subtitle="Options available for your region and points." />
        <span className="hidden items-center gap-2 text-xs font-medium text-ink-3 sm:inline-flex">
          <span className="zs-logo-plate w-7 h-7">
            <img src={giftcardIcon} alt="" className="w-4 h-4" />
          </span>
          Gift Cards
        </span>
      </div>

      <div className="zs-card-body">
        <div className="form-group">
          <div className="grid grid-cols-1 gap-2 pr-1 overflow-y-auto sm:grid-cols-2 max-h-[28rem] zs-scroll">
            {listed?.map((currentItem, i) => {
              if (isEligible(currentItem)) {
                const isSelected = String(selectedBrand) === String(currentItem.id);
                return (
                  <label
                    key={currentItem.id}
                    className={`relative items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      showAll || i < 5 ? "flex" : "hidden"
                    } ${
                      isSelected
                        ? "border-primary bg-primary-soft ring-1 ring-primary"
                        : "border-line bg-surface hover:border-line-strong hover:bg-surface-hover"
                    }`}
                  >
                    <span className="w-16 h-10 px-1.5 zs-logo-plate shrink-0">
                      <img src={currentItem.logo} alt={currentItem.name} className="object-contain w-full h-full" />
                    </span>
                    <span className="flex-1 min-w-0 text-sm font-medium leading-snug text-ink">{currentItem.name}</span>
                    <input
                      type="radio"
                      name="neoBrands"
                      value={currentItem.id}
                      onChange={onChange}
                      className="w-4 h-4 cursor-pointer required accent-primary shrink-0"
                    />
                  </label>
                );
              }
              return null;
            })}
          </div>

          {!isLoading && !hasOptions && (
            <EmptyState
              icon={<FiGift />}
              title="No gift cards available"
              description="No gift card matches your region and the points being redeemed right now."
            />
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
            {!showAll && brands?.length > 3 && (
              <button onClick={onShowMore} className="inline-flex items-center gap-1 text-sm font-medium zs-link">
                View more gift cards
                <FiChevronDown />
              </button>
            )}
            {showAll && (
              <button onClick={onShowLess} className="inline-flex items-center gap-1 text-sm font-medium zs-link">
                Show Less gift cards
                <FiChevronUp />
              </button>
            )}
          </div>
          {error ? (
            <p className="mt-2 zs-error" id="emai_err_msg">
              {error}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
};
